import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, ActionCreator, Creator } from '@ngrx/store';
import { forkJoin, of } from 'rxjs';
import { catchError, exhaustMap, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { UserNotificationAction } from '@arc/arc-shared-frontend';
import {
  AutopayEligibilityResponse,
  PaymentSchedulerServiceFactory,
} from '@arc/unified-payments-frontend/shared-payment';

const isUnenrolled = (response: AutopayEligibilityResponse): boolean =>
  !!response?.autopayEligibility?.buyerManagedEligible &&
  !response?.autopaymentInstruction;

/**
 * Factory that creates a reusable NgRx functional effect checking per-account Autopay
 * eligibility (BTPAY-7160). Listens for a configurable trigger action carrying
 * tenantId + accountNumbers, calls the per-account eligibility endpoint for each account
 * via PaymentSchedulerServiceFactory, then dispatches the provided success action with the
 * resulting unenrolledAccounts, or the provided error action plus the repo's standard generic
 * error toast (matches the `updatePaymentAccountExpirationAction$` pattern in btpay.effects.ts —
 * per CLAUDE.md this is a pre-redirect failure and must surface to the buyer, not just hide the
 * banner silently).
 *
 * Usage (POI, POA, international — each passes its own trigger action; success/error are
 * shared since the resulting state is flow-agnostic):
 *
 *   export const poiCheckAutopayEligibilityEffect = createCheckAutopayEligibilityEffect({
 *     triggerAction: PoiPaymentConfirmationComponentActions.checkAutopayEligibility,
 *     successAction: CheckAutopayEligibilitySuccessAction,
 *     errorAction: CheckAutopayEligibilityErrorAction,
 *   });
 */
export function createCheckAutopayEligibilityEffect<
  A extends Action<string> & { tenantId: string; accountNumbers: string[] },
>(config: {
  triggerAction: ActionCreator<string, Creator<any[], A>>;
  successAction: ActionCreator;
  errorAction: ActionCreator;
}) {
  return createEffect(
    (
      actions$ = inject(Actions),
      paymentSchedulerServiceFactory = inject(PaymentSchedulerServiceFactory),
      translateService = inject(TranslateService),
    ) =>
      actions$.pipe(
        ofType(config.triggerAction),
        exhaustMap((action) => {
          const schedulerService = paymentSchedulerServiceFactory.create();
          return forkJoin(
            action.accountNumbers.map((accountNumber) =>
              schedulerService
                .getAutopayEligibilityV1(action.tenantId, accountNumber)
                .pipe(map((response) => ({ accountNumber, response }))),
            ),
          ).pipe(
            map(
              (results) =>
                config.successAction({
                  unenrolledAccounts: results
                    .filter(({ response }) => isUnenrolled(response))
                    .map(({ accountNumber }) => accountNumber),
                }) as Action,
            ),
            catchError(() =>
              of(
                config.errorAction() as Action,
                new UserNotificationAction({
                  type: 'error',
                  message: translateService.instant(
                    'ARC_PAYMENT_INTL_GENERIC_ERROR',
                  ) as string,
                }),
              ),
            ),
          );
        }),
      ),
    { functional: true },
  );
}
